import type { OverrideProp, OverrideViewport } from '@evo-hub/shared';

// ── Historial de la sesión de edición ────────────────────────────────────────
//
// Cmd+Z hoy delega en el historial del propio contenteditable, que solo conoce lo
// que pasó dentro de un elemento y no sabe nada de un cambio de clase. Acá se
// registra la sesión entera, en orden, con cómo revertir cada paso.
//
// Sin DOM ni React a propósito: lo que puede equivocarse es la contabilidad
// —qué se deshace primero, qué queda pendiente de guardar, qué pasa al rehacer—
// y eso se puede probar sin montar nada.

/** Qué le pasó a un elemento como bloque, no a su contenido. */
export interface StructureEntry {
  type: 'structure';
  targetId: string;
  action: 'duplicate' | 'remove' | 'move';
  /** duplicate: id viejo → id nuevo, para el elemento y cada descendiente. */
  idMap?: Record<string, string>;
  /** move: hacia dónde se movió. Deshacer es moverlo al revés. */
  direction?: 'up' | 'down';
  /**
   * remove: lo necesario para volver a ponerlo donde estaba. Se guarda el HTML y
   * no una referencia al nodo porque el historial tiene que poder describirse
   * sin depender de que ese nodo siga vivo en algún lado.
   */
  html?: string;
  parentId?: string | null;
  nextId?: string | null;
}

export type HistoryEntry =
  | {
      type: 'text';
      targetId: string;
      kind: 'rich' | 'own';
      /** Contenido antes de este paso. Revertir = volver a esto. */
      before: string;
      after: string;
    }
  | {
      type: 'class';
      targetId: string;
      add: string[];
      remove: string[];
    }
  | {
      /** Destino de un enlace. */
      type: 'href';
      targetId: string;
      before: string;
      after: string;
    }
  | {
      /** Párrafo ↔ encabezado. */
      type: 'tag';
      targetId: string;
      before: string;
      after: string;
    }
  | {
      /**
       * Un ajuste de estilo por vista. `before`/`after` son el valor CSS, o null
       * cuando no había o cuando se borra: deshacer tiene que poder devolver el
       * elemento al estado de "sin ajuste", que no es lo mismo que un valor.
       */
      type: 'style';
      targetId: string;
      viewport: OverrideViewport;
      prop: OverrideProp;
      before: string | null;
      after: string | null;
    }
  | StructureEntry;

/** Cambio pendiente de guardar, ya consolidado por elemento. */
export interface PendingChange {
  targetId: string;
  type: 'text' | 'class' | 'href' | 'tag' | 'structure' | 'style';
  /** En texto, destino y etiqueta: el valor original, de antes de tocar nada. */
  original?: string;
  value?: string;
  kind?: 'rich' | 'own';
  /** Solo en clases: las que quedaron agregadas y quitadas, netas. */
  added?: string[];
  removed?: string[];
  /** Solo en estilo. */
  viewport?: OverrideViewport;
  prop?: OverrideProp;
  /** Solo en estructura. */
  action?: 'duplicate' | 'remove' | 'move';
  direction?: 'up' | 'down';
  idMap?: Record<string, string>;
}

/**
 * Cuánto tiempo se agrupan ediciones de texto seguidas sobre el mismo elemento.
 *
 * Sin esto cada tecla sería un paso de deshacer y Cmd+Z borraría de a una letra.
 * Con una ventana, escribir una frase y deshacer la saca entera, que es lo que
 * hace cualquier editor.
 */
export const COALESCE_MS = 1_000;

/**
 * Consolida un tramo sin pasos de estructura: un cambio por elemento y tipo.
 *
 * Un elemento que volvió a su valor original no aparece — no hay nada que
 * guardar. Lo mismo una clase agregada y después quitada.
 */
function foldTramo(entries: HistoryEntry[]): PendingChange[] {
  const simples = new Map<string, PendingChange>();
  const clases = new Map<string, { added: Set<string>; removed: Set<string> }>();
  const estilos = new Map<string, PendingChange>();

  for (const e of entries) {
    if (e.type === 'class') {
      const acc = clases.get(e.targetId) ?? { added: new Set<string>(), removed: new Set<string>() };
      // Cada operación se cancela con su contraria, no se acumula: agregar una
      // clase y después quitarla deja el elemento como estaba, y mandar al
      // servidor "quitá esto" de algo que nunca tuvo es ruido que además
      // aparecería listado como cambio pendiente.
      for (const c of e.remove) {
        if (acc.added.delete(c)) continue;
        acc.removed.add(c);
      }
      for (const c of e.add) {
        if (acc.removed.delete(c)) continue;
        acc.added.add(c);
      }
      clases.set(e.targetId, acc);
      continue;
    }
    if (e.type === 'structure') continue;

    if (e.type === 'style') {
      // La clave lleva vista y propiedad: el mismo elemento puede tener un
      // tamaño en mobile y otro en desktop, y son dos cambios distintos que
      // conviven, no uno pisando al otro.
      const clave = `style:${e.targetId}:${e.viewport}:${e.prop}`;
      const previo = estilos.get(clave);
      // `original` y `value` se OMITEN cuando no hay valor, en vez de ponerlos
      // en undefined: acá "sin ajuste" es un estado real —el elemento no tiene
      // nada puesto en esa vista— y tiene que poder distinguirse.
      const original = previo?.original ?? e.before ?? null;
      const p: PendingChange = {
        targetId: e.targetId, type: 'style',
        viewport: e.viewport, prop: e.prop,
        ...(original !== null ? { original } : {}),
        ...(e.after !== null ? { value: e.after } : {}),
      };
      estilos.set(clave, p);
      continue;
    }

    const clave = `${e.type}:${e.targetId}`;
    const previo = simples.get(clave);
    const p: PendingChange = {
      targetId: e.targetId,
      type: e.type,
      // El original es el del PRIMER paso sobre ese elemento.
      original: previo?.original ?? e.before,
      value: e.after,
    };
    if (e.type === 'text') p.kind = e.kind;
    simples.set(clave, p);
  }

  const out: PendingChange[] = [];
  for (const c of simples.values()) {
    // Volver al valor original no es un cambio.
    if (c.original !== c.value) out.push(c);
  }
  for (const c of estilos.values()) {
    // Volver a como estaba —incluido "sin ajuste"— no es un cambio.
    if (c.original !== c.value) out.push(c);
  }
  for (const [targetId, acc] of clases) {
    if (acc.added.size === 0 && acc.removed.size === 0) continue;
    out.push({
      targetId, type: 'class',
      added: [...acc.added].sort(),
      removed: [...acc.removed].sort(),
    });
  }
  return out;
}

export class SessionHistory {
  private entries: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  /** Momento del último push de texto por elemento, para agrupar. */
  private lastTextAt = new Map<string, number>();

  /**
   * Registra un paso.
   *
   * Dos ediciones de texto seguidas sobre el mismo elemento se funden en una: se
   * conserva el `before` de la primera y el `after` de la última, así deshacer
   * vuelve al punto donde se empezó a escribir y no a la letra anterior.
   */
  push(entry: HistoryEntry, now: number): void {
    // Cualquier paso nuevo invalida lo que había para rehacer: la línea de
    // tiempo se bifurcó.
    this.redoStack = [];

    if (entry.type === 'text') {
      const ultimo = this.entries[this.entries.length - 1];
      const desde = this.lastTextAt.get(entry.targetId) ?? -Infinity;
      if (
        ultimo?.type === 'text' &&
        ultimo.targetId === entry.targetId &&
        now - desde < COALESCE_MS
      ) {
        ultimo.after = entry.after;
        this.lastTextAt.set(entry.targetId, now);
        return;
      }
      this.lastTextAt.set(entry.targetId, now);
    }
    this.entries.push(entry);
  }

  /**
   * Saca un paso de estructura de la línea de tiempo y lo devuelve.
   *
   * Revertir un cambio puntual de texto se resuelve solo: se escribe otro paso
   * que vuelve al valor original y los dos se consolidan en nada. Un paso de
   * estructura no se consolida con nada —duplicar y borrar la copia son dos
   * hechos distintos— así que para que revertirlo no deje un borrado pendiente
   * hay que sacarlo de verdad.
   */
  dropStructure(targetId: string, action: 'duplicate' | 'remove' | 'move'): StructureEntry | null {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i];
      if (e?.type === 'structure' && e.targetId === targetId && e.action === action) {
        this.entries.splice(i, 1);
        // La línea de tiempo cambió: lo que había para rehacer ya no encaja.
        this.redoStack = [];
        return e;
      }
    }
    return null;
  }

  /** Saca el último paso y lo devuelve para que quien llame aplique su inverso. */
  undo(): HistoryEntry | null {
    const e = this.entries.pop();
    if (!e) return null;
    this.redoStack.push(e);
    // Cortar la agrupación: lo próximo que se escriba es un paso nuevo.
    this.lastTextAt.clear();
    return e;
  }

  /** Devuelve el paso a rehacer. */
  redo(): HistoryEntry | null {
    const e = this.redoStack.pop();
    if (!e) return null;
    this.entries.push(e);
    this.lastTextAt.clear();
    return e;
  }

  get canUndo(): boolean { return this.entries.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }
  get size(): number { return this.entries.length; }

  clear(): void {
    this.entries = [];
    this.redoStack = [];
    this.lastTextAt.clear();
  }

  /**
   * Cambios pendientes, consolidados por elemento.
   *
   * Es lo que se manda al guardar Y lo que lista el panel: una sola fuente, para
   * que no puedan discrepar. Un elemento que volvió a su texto original no
   * aparece — no hay nada que guardar.
   */
  pending(): PendingChange[] {
    // Los pasos de estructura NO se pueden reordenar respecto de los de
    // contenido: duplicar un bloque y después editar su texto no es lo mismo que
    // al revés. Por eso se corta la lista en cada paso de estructura y se
    // consolida solo ADENTRO de cada tramo; los tramos salen en orden.
    const out: PendingChange[] = [];
    let tramo: HistoryEntry[] = [];

    const volcar = (): void => {
      if (tramo.length > 0) out.push(...foldTramo(tramo));
      tramo = [];
    };

    for (const e of this.entries) {
      if (e.type === 'structure') {
        volcar();
        const p: PendingChange = { targetId: e.targetId, type: 'structure', action: e.action };
        if (e.direction) p.direction = e.direction;
        if (e.idMap) p.idMap = e.idMap;
        out.push(p);
        continue;
      }
      tramo.push(e);
    }
    volcar();
    return out;
  }
}
